import { PersistentMemoryTracker } from "./persistentMemoryTracker";

interface MemorySnapshot {
  timestamp: number;
  usedJSHeapSize: number;
  totalJSHeapSize: number;
  jsHeapSizeLimit: number;
  domNodes: number;
  eventListeners: number;
  activeTimers: number;
}

interface ComponentRenderInfo {
  componentName: string;
  renderCount: number;
  lastRender: number;
  memoryAtRender: number;
  memoryIncrease: number;
}

interface MemoryLeakReport {
  isLeakDetected: boolean;
  leakSeverity: 'low' | 'medium' | 'high' | 'critical';
  memoryIncrease: number;
  domNodeIncrease: number;
  eventListenerIncrease: number;
  timerIncrease: number;
  problematicComponents: string[];
  recommendations: string[];
}

export class EnhancedMemoryLeakDetector {
  private static instance: EnhancedMemoryLeakDetector;
  private memorySnapshots: MemorySnapshot[] = [];
  private renderCounts: Map<string, number> = new Map();
  private componentRenders: Map<string, ComponentRenderInfo> = new Map();
  private originalConsoleLog: (...args: unknown[]) => void;
  private isInterceptingConsole: boolean = false;
  private tracker: PersistentMemoryTracker;
  private monitoringInterval: NodeJS.Timeout | null = null;
  private leakThresholds = {
    memoryIncrease: 20, // MB
    domNodeIncrease: 100,
    eventListenerIncrease: 10,
    timerIncrease: 5,
    renderThreshold: 20
  };
  private activeEventListeners = new Set<string>();
  private activeTimers = new Set<number>();

  private constructor() {
    this.tracker = PersistentMemoryTracker.getInstance();
    this.originalConsoleLog = console.log;
    this.interceptConsoleLog();
    this.startAdvancedMonitoring();
    console.log('🔍 Enhanced Memory Leak Detector started - monitoring memory patterns, DOM nodes, and component renders');
  }

  public static getInstance(): EnhancedMemoryLeakDetector {
    if (!EnhancedMemoryLeakDetector.instance) {
      EnhancedMemoryLeakDetector.instance = new EnhancedMemoryLeakDetector();
    }
    return EnhancedMemoryLeakDetector.instance;
  }

  private interceptConsoleLog() {
    if (this.isInterceptingConsole) return;

    console.log = (...args: unknown[]) => {
      this.originalConsoleLog.apply(console, args);
      const message = args.map(arg => typeof arg === 'string' ? arg : JSON.stringify(arg)).join(' ');
      this.trackComponentRender(message);
    };
    this.isInterceptingConsole = true;
  }

  private getCurrentMemory(): { used: number; total: number; limit: number } {
    if (typeof window !== 'undefined' && 'memory' in performance) {
      const memory = (performance as Performance & { memory?: { usedJSHeapSize: number; totalJSHeapSize: number; jsHeapSizeLimit: number } }).memory;
      if (memory) {
        return {
          used: Math.round(memory.usedJSHeapSize / 1024 / 1024),
          total: Math.round(memory.totalJSHeapSize / 1024 / 1024),
          limit: Math.round(memory.jsHeapSizeLimit / 1024 / 1024),
        };
      }
    }
    return { used: 0, total: 0, limit: 0 };
  }

  private getDOMNodeCount(): number {
    if (typeof document !== 'undefined') {
      return document.querySelectorAll('*').length;
    }
    return 0;
  }

  private getEventListenersCount(): number {
    return this.activeEventListeners.size;
  }

  private getActiveTimersCount(): number {
    return this.activeTimers.size;
  }

  private startAdvancedMonitoring() {
    // Take initial snapshot
    this.takeSnapshot();
    
    // Monitor every 2 seconds
    this.monitoringInterval = setInterval(() => {
      this.takeSnapshot();
      this.analyzeMemoryPatterns();
    }, 2000);

    // Intercept setTimeout and setInterval
    this.interceptTimers();
    
    // Intercept addEventListener
    this.interceptEventListeners();

    console.log('🔍 Advanced monitoring started - tracking memory, DOM nodes, timers, and event listeners');
  }

  private interceptTimers() {
    if (typeof window === 'undefined') return;

    const originalSetTimeout = window.setTimeout;
    const originalSetInterval = window.setInterval;
    const originalClearTimeout = window.clearTimeout;
    const originalClearInterval = window.clearInterval;

    window.setTimeout = ((callback: () => void, delay: number, ...args: unknown[]) => {
      const id = originalSetTimeout(callback, delay, ...args);
      this.activeTimers.add(id);
      
      // Auto-cleanup after delay + buffer
      originalSetTimeout(() => {
        this.activeTimers.delete(id);
      }, delay + 1000);
      
      return id;
    }) as typeof setTimeout;

    window.setInterval = ((callback: () => void, delay: number, ...args: unknown[]) => {
      const id = originalSetInterval(callback, delay, ...args);
      this.activeTimers.add(id);
      return id;
    }) as typeof setInterval;

    window.clearTimeout = ((id: number) => {
      this.activeTimers.delete(id);
      return originalClearTimeout(id);
    }) as typeof clearTimeout;

    window.clearInterval = ((id: number) => {
      this.activeTimers.delete(id);
      return originalClearInterval(id);
    }) as typeof clearInterval;
  }

  private interceptEventListeners() {
    if (typeof window === 'undefined') return;

    const originalAddEventListener = EventTarget.prototype.addEventListener;
    const originalRemoveEventListener = EventTarget.prototype.removeEventListener;

    EventTarget.prototype.addEventListener = function(type: string, listener: EventListenerOrEventListenerObject, options?: boolean | AddEventListenerOptions) {
      const self = (typeof this === 'undefined' || this === null) ? (typeof window !== 'undefined' ? window : undefined) : this;
      const ctorName = (self as EventTarget)?.constructor?.name || (self === window ? 'Window' : self === document ? 'Document' : 'GlobalEventTarget');
      const key = `${ctorName}-${type}`;
      EnhancedMemoryLeakDetector.getInstance().activeEventListeners.add(key);
      const context = (self ?? this) as EventTarget;
      return originalAddEventListener.call(context, type, listener, options);
    };

    EventTarget.prototype.removeEventListener = function(type: string, listener: EventListenerOrEventListenerObject, options?: boolean | EventListenerOptions) {
      const self = (typeof this === 'undefined' || this === null) ? (typeof window !== 'undefined' ? window : undefined) : this;
      const ctorName = (self as EventTarget)?.constructor?.name || (self === window ? 'Window' : self === document ? 'Document' : 'GlobalEventTarget');
      const key = `${ctorName}-${type}`;
      EnhancedMemoryLeakDetector.getInstance().activeEventListeners.delete(key);
      const context = (self ?? this) as EventTarget;
      return originalRemoveEventListener.call(context, type, listener, options);
    };
  }

  private trackComponentRender(message: string) {
    const timestamp = Date.now();
    const memory = this.getCurrentMemory();

    // Extract component name from log message - look for specific patterns
    const patterns = [
      /🔄\s+(\w+)/,  // 🔄 ComponentName
      /🔄\s+(\w+)\s+component/,  // 🔄 ComponentName component
      /🔄\s+(\w+)\s+re-rendered/,  // 🔄 ComponentName re-rendered
      /🔄\s+(\w+)\s+rendered/,  // 🔄 ComponentName rendered
      /Layout useEffect - Session data/, // Specific for Layout
      /Fetching session data/, // Specific for SessionContext
      /Session API response/, // Specific for SessionContext
      /Session data received/, // Specific for SessionContext
      /Drawer - Docs group filtering/, // Specific for Drawer
      /Drawer - filteredDrawerRoutes/, // Specific for Drawer
      /DropdownCreate: open = false onOpenChange = true/, // Specific for DropdownCreate
      /Hook received data/, // Specific for contractManageHooks
      /Converted to Date/, // Specific for contractManageHooks
      /🎭 Datos reales devueltos/, // Specific for contractsListHooks
      /🔄 Passing to List/, // Specific for page.tsx
      /🔄 List component/, // Specific for page.tsx
      /🔄 useContracts queryFn called/, // Specific for contractsListHooks
      /🎭 Tour NO activo/, // Specific for contractsListHooks
      /Contracts data loaded/, // Specific for page.tsx
      /Sample patients/, // Specific for page.tsx
    ];

    let componentName = null;
    for (const pattern of patterns) {
      const match = message.match(pattern);
      if (match) {
        componentName = match[1] || pattern.source.replace(/\\s\+\(\\w\+\).*/, '').replace(/\\b/g, '');
        if (componentName.includes('useEffect')) componentName = 'useEffect';
        if (componentName.includes('Layout')) componentName = 'Layout';
        if (componentName.includes('Fetching')) componentName = 'SessionContext';
        if (componentName.includes('Session API')) componentName = 'SessionContext';
        if (componentName.includes('Session data')) componentName = 'SessionContext';
        if (componentName.includes('Drawer - Docs')) componentName = 'Drawer';
        if (componentName.includes('Drawer - filtered')) componentName = 'Drawer';
        if (componentName.includes('DropdownCreate')) componentName = 'DropdownCreate';
        if (componentName.includes('Hook received')) componentName = 'contractManageHooks';
        if (componentName.includes('Converted to Date')) componentName = 'contractManageHooks';
        if (componentName.includes('Datos reales')) componentName = 'contractsListHooks';
        if (componentName.includes('Passing to List')) componentName = 'Passing';
        if (componentName.includes('List component')) componentName = 'List';
        if (componentName.includes('useContracts')) componentName = 'contractsListHooks';
        if (componentName.includes('Tour NO activo')) componentName = 'contractsListHooks';
        if (componentName.includes('Contracts data loaded')) componentName = 'ContractsContent';
        if (componentName.includes('Sample patients')) componentName = 'ContractsContent';
        break;
      }
    }

    if (componentName) {
      const currentCount = this.renderCounts.get(componentName) || 0;
      this.renderCounts.set(componentName, currentCount + 1);

      // Track detailed render info
      const existingData = this.componentRenders.get(componentName);
      const memoryIncrease = existingData ? memory.used - existingData.memoryAtRender : 0;
      
      this.componentRenders.set(componentName, {
        componentName,
        renderCount: currentCount + 1,
        lastRender: timestamp,
        memoryAtRender: memory.used,
        memoryIncrease: memoryIncrease
      });

      // Alert on excessive renders
      if (currentCount > 0 && currentCount % 10 === 0) {
        console.warn(`🚨 ${componentName} has rendered ${currentCount + 1} times! Memory: ${memory.used}MB`);
      }
    }
  }

  private takeSnapshot() {
    const memory = this.getCurrentMemory();
    const snapshot: MemorySnapshot = {
      timestamp: Date.now(),
      usedJSHeapSize: memory.used * 1024 * 1024,
      totalJSHeapSize: memory.total * 1024 * 1024,
      jsHeapSizeLimit: memory.limit * 1024 * 1024,
      domNodes: this.getDOMNodeCount(),
      eventListeners: this.getEventListenersCount(),
      activeTimers: this.getActiveTimersCount(),
    };

    this.memorySnapshots.push(snapshot);
    if (this.memorySnapshots.length > 20) { // Keep last 20 snapshots
      this.memorySnapshots.shift();
    }
  }

  private analyzeMemoryPatterns() {
    if (this.memorySnapshots.length < 3) return;

    const recent = this.memorySnapshots.slice(-3);
    const older = this.memorySnapshots.slice(-6, -3);
    
    if (older.length === 0) return;

    const memoryIncrease = recent[recent.length - 1].usedJSHeapSize - older[0].usedJSHeapSize;
    const domNodeIncrease = recent[recent.length - 1].domNodes - older[0].domNodes;
    const eventListenerIncrease = recent[recent.length - 1].eventListeners - older[0].eventListeners;
    const timerIncrease = recent[recent.length - 1].activeTimers - older[0].activeTimers;

    // Check for memory leak patterns
    if (memoryIncrease > this.leakThresholds.memoryIncrease * 1024 * 1024) {
      console.warn(`🚨 MEMORY LEAK DETECTED! Memory increased by ${Math.round(memoryIncrease / 1024 / 1024)}MB`);
      this.generateLeakReport();
    }

    if (domNodeIncrease > this.leakThresholds.domNodeIncrease) {
      console.warn(`🚨 DOM LEAK DETECTED! DOM nodes increased by ${domNodeIncrease}`);
    }

    if (eventListenerIncrease > this.leakThresholds.eventListenerIncrease) {
      console.warn(`🚨 EVENT LISTENER LEAK DETECTED! Event listeners increased by ${eventListenerIncrease}`);
    }

    if (timerIncrease > this.leakThresholds.timerIncrease) {
      console.warn(`🚨 TIMER LEAK DETECTED! Active timers increased by ${timerIncrease}`);
    }
  }

  private generateLeakReport(): MemoryLeakReport {
    const recent = this.memorySnapshots[this.memorySnapshots.length - 1];
    const initial = this.memorySnapshots[0];
    
    const memoryIncrease = Math.round((recent.usedJSHeapSize - initial.usedJSHeapSize) / 1024 / 1024);
    const domNodeIncrease = recent.domNodes - initial.domNodes;
    const eventListenerIncrease = recent.eventListeners - initial.eventListeners;
    const timerIncrease = recent.activeTimers - initial.activeTimers;

    const problematicComponents = Array.from(this.componentRenders.entries())
      .filter(([, data]) => data.renderCount > this.leakThresholds.renderThreshold)
      .map(([name]) => name);

    const isLeakDetected = memoryIncrease > this.leakThresholds.memoryIncrease || 
                          domNodeIncrease > this.leakThresholds.domNodeIncrease ||
                          eventListenerIncrease > this.leakThresholds.eventListenerIncrease ||
                          timerIncrease > this.leakThresholds.timerIncrease;

    let leakSeverity: 'low' | 'medium' | 'high' | 'critical' = 'low';
    if (memoryIncrease > 100) leakSeverity = 'critical';
    else if (memoryIncrease > 50) leakSeverity = 'high';
    else if (memoryIncrease > 20) leakSeverity = 'medium';

    const recommendations = [];
    if (memoryIncrease > 20) recommendations.push('Review component cleanup and useEffect dependencies');
    if (domNodeIncrease > 100) recommendations.push('Check for detached DOM nodes and proper element removal');
    if (eventListenerIncrease > 10) recommendations.push('Ensure event listeners are properly removed in cleanup functions');
    if (timerIncrease > 5) recommendations.push('Check for uncleaned setTimeout/setInterval calls');
    if (problematicComponents.length > 0) recommendations.push(`Optimize frequently rendering components: ${problematicComponents.join(', ')}`);

    return {
      isLeakDetected,
      leakSeverity,
      memoryIncrease,
      domNodeIncrease,
      eventListenerIncrease,
      timerIncrease,
      problematicComponents,
      recommendations
    };
  }

  public getReport() {
    const leakReport = this.generateLeakReport();
    
    return {
      memorySnapshots: this.memorySnapshots,
      renderCounts: Object.fromEntries(this.renderCounts),
      componentRenders: Object.fromEntries(this.componentRenders),
      leakReport,
      currentMemory: this.getCurrentMemory(),
      domNodes: this.getDOMNodeCount(),
      eventListeners: this.getEventListenersCount(),
      activeTimers: this.getActiveTimersCount(),
    };
  }

  public clearData() {
    this.memorySnapshots = [];
    this.renderCounts.clear();
    this.componentRenders.clear();
    this.activeEventListeners.clear();
    this.activeTimers.clear();
    this.tracker.clearData();
    console.log('🗑️ Enhanced Memory Leak Detector data cleared.');
  }

  public stopMonitoring() {
    if (this.monitoringInterval) {
      clearInterval(this.monitoringInterval);
      this.monitoringInterval = null;
    }
    console.log('🛑 Memory leak monitoring stopped.');
  }
}

// Initialize the detector - ONLY in development to avoid production overhead
// Temporarily disabled to avoid global timer/event interception while debugging other flows.
// Re-enable by uncommenting this block.
// if (typeof window !== 'undefined' && process.env.NODE_ENV === 'development') {
//   EnhancedMemoryLeakDetector.getInstance();
// }
